<template>
  <v-container fluid class="neoparent-login d-flex flex-column align-center">
    <div class="login-form" style="max-width: 400px; width: 100%;">
      <div class="language-switcher mb-4">
        <div class="language-switcher__label mb-2">
          {{ $t("app.settings.general.display-language") }}
        </div>
        <v-select
          v-model="selectedLocale"
          :items="languageOptions"
          item-text="label"
          item-value="code"
          outlined
          dense
          hide-details
        />
      </div>
      <h2 class="text-center mb-6">{{ $t("auth.login.title") }}</h2>

      <div class="error mb-4" v-if="error">
        <p>{{ error }}</p>
      </div>

      <div class="status-message mb-4" v-if="statusMessage">
        <p>{{ statusMessage }}</p>
      </div>

      <form @submit.prevent="handleSubmit">
        <InputField
          v-model="email"
          :labelTitle="$t('auth.login.email')"
          type="email"
          required
          :placeholder="$t('auth.login.emailPlaceholder')"
          margin="0 auto 16px auto"
          :disabled="isBusy || currentStep === 2"
        ></InputField>
        <template v-if="currentStep === 1">
          <LoadingButton v-if="isCheckingChannels" margin="0 auto 16px auto"></LoadingButton>
          <Button
            v-else
            type="submit"
            width="100%"
            margin="0 auto 16px auto"
            :title="$t('button.next')"
            :disabled="isBusy"
          ></Button>
        </template>

        <template v-else>
          <div class="d-flex mb-4" style="gap: 12px;" v-if="isRequestDisabled && cooldownTimer">
            {{$t("auth.login.resendIn", { seconds: resendCooldownSeconds })}}
          </div>
          <div class="d-flex mb-4" style="gap: 12px;" v-if="!isRequestDisabled">
            {{$t('auth.login.sendCodeBy')}}
          </div>

          <div class="d-flex mb-4" style="gap: 12px;">
            <Button
              type="button"
              @click.native.prevent="requestCode('email')"
              :title="getRequestButtonTitle('email')"
              width="100%"
              v-if="!isRequestDisabled"
            ></Button>
            <Button
              type="button"
              @click.native.prevent="requestCode('sms')"
              :title="getRequestButtonTitle('sms')"
              width="100%"
              v-if="!isRequestDisabled && smsAvailable"
            ></Button>
            <Button
              type="button"
              @click.native.prevent="requestCode('whatsapp')"
              :title="getRequestButtonTitle('whatsapp')"
              width="100%"
              v-if="!isRequestDisabled && whatsappAvailable"
            ></Button>
          </div>

          <p
            v-if="canVerifyCode && resendCooldownSeconds === 0"
            class="email-check-message mb-4"
          >
            {{ $t("auth.login.checkEmailAddress") }}
          </p>

          <Button
            type="button"
            width="100%"
            margin="0 auto 16px auto"
            :title="$t('button.back')"
            :disabled="isBusy"
            @click.native.prevent="goBackToEmailStep"
          ></Button>

          <template v-if="canVerifyCode">
          <InputField
            v-model="code"
            :labelTitle="$t('auth.login.code')"
            type="text"
            required
            :placeholder="$t('auth.login.codePlaceholder')"
            margin="0 auto 16px auto"
            :disabled="isBusy"
          ></InputField>

          <LoadingButton v-if="isVerifying" margin="0 auto 16px auto"></LoadingButton>
          <Button
            v-else
            type="submit"
            width="100%"
            margin="0 auto 16px auto"
            :title="$t('auth.login.loginWithCode')"
            :disabled="isBusy"
          ></Button>
          </template>
        </template>
      </form>
    </div>
  </v-container>
</template>

<script>
import { neoparentAuth } from "@/api";
import { getSupportedLocales } from "@/helpers";

export default {
  name: "neoparent-login",
  data() {
    return {
      email: "",
      code: "",
      error: "",
      statusMessage: "",
      isSendingEmail: false,
      isSendingSms: false,
      isSendingWhatsapp: false,
      isCheckingChannels: false,
      isVerifying: false,
      canVerifyCode: false,
      currentStep: 1,
      smsAvailable: false,
      whatsappAvailable: false,
      resendCooldownSeconds: 0,
      resendCooldownDuration: 30,
      cooldownTimer: null,
    };
  },
  beforeDestroy() {
    this.stopCooldown();
  },
  watch: {
    email() {
      this.canVerifyCode = false;
      this.code = "";
      this.statusMessage = "";
      this.error = "";
    }
  },
  computed: {
    isBusy() {
      return this.isSendingEmail || this.isSendingSms || this.isSendingWhatsapp || this.isCheckingChannels || this.isVerifying;
    },
    isRequestDisabled() {
      return this.isBusy || this.resendCooldownSeconds > 0;
    },
    languageOptions() {
      return getSupportedLocales("", this.selectedLocale).map(code => ({
        code,
        label: this.$t(`general.languages.${code}`)
      }));
    },
    selectedLocale: {
      get() {
        return this.normalizeLocale(this.$i18n.locale);
      },
      set(lang) {
        this.switchLanguage(lang);
      }
    }
  },
  methods: {
    handleSubmit() {
      if (this.currentStep === 1) {
        this.checkAvailableChannels();
        return;
      }

      this.verifyCode();
    },
    async checkAvailableChannels() {
      if (!this.email) {
        this.error = this.$t("auth.login.emailRequired");
        return;
      }

      this.isCheckingChannels = true;
      this.error = "";
      this.statusMessage = "";
      this.canVerifyCode = false;
      this.code = "";

      try {
        const response = await neoparentAuth.checkCodeChannels(this.email);
        this.smsAvailable = !!response?.data?.smsAvailable;
        this.whatsappAvailable = this.smsAvailable && !!response?.data?.whatsappAvailable;
        this.currentStep = 2;
      } catch (e) {
        this.error = this.$t("auth.login.sendCodeError");
      } finally {
        this.isCheckingChannels = false;
      }
    },
    goBackToEmailStep() {
      if (this.isBusy) {
        return;
      }

      this.stopCooldown();
      this.currentStep = 1;
      this.resendCooldownSeconds = 0;
      this.canVerifyCode = false;
      this.code = "";
      this.statusMessage = "";
      this.error = "";
      this.smsAvailable = false;
      this.whatsappAvailable = false;
    },
    normalizeLocale(lang) {
      return lang === "be" ? "nl-be" : lang;
    },
    switchLanguage(lang) {
      const locale = this.normalizeLocale(lang);

      this.$i18n.locale = locale;
      localStorage.setItem("locale", locale);

      if (this.$moment) {
        this.$moment.locale(locale);
      }
    },
    getRequestButtonTitle(channel) {
      if (channel === "email" && this.isSendingEmail) {
        return this.$t("auth.login.sending");
      }

      if (channel === "sms" && this.isSendingSms) {
        return this.$t("auth.login.sending");
      }

      if (channel === "whatsapp" && this.isSendingWhatsapp) {
        return this.$t("auth.login.sending");
      }

      if (this.resendCooldownSeconds > 0) {
        return this.$t("auth.login.resendIn", { seconds: this.resendCooldownSeconds });
      }

      return channel === "email"
        ? this.$t("auth.login.email")
        : channel === "sms"
          ? this.$t("auth.login.sms")
          : "WhatsApp";
    },
    startCooldown() {
      this.stopCooldown();
      this.resendCooldownSeconds = this.resendCooldownDuration;

      this.cooldownTimer = window.setInterval(() => {
        if (this.resendCooldownSeconds <= 1) {
          this.resendCooldownSeconds = 0;
          this.stopCooldown();
          return;
        }

        this.resendCooldownSeconds -= 1;
      }, 1000);
    },
    stopCooldown() {
      if (this.cooldownTimer) {
        window.clearInterval(this.cooldownTimer);
        this.cooldownTimer = null;
      }
    },
    async requestCode(channel) {
      if (this.resendCooldownSeconds > 0) {
        return;
      }

      if (!this.email) {
        this.error = this.$t("auth.login.emailRequired");
        return;
      }

      this.isSendingEmail = channel === "email";
      this.isSendingSms = channel === "sms";
      this.isSendingWhatsapp = channel === "whatsapp";
      this.error = "";
      this.statusMessage = "";
      this.code = "";
      this.canVerifyCode = false;

      try {
        if (channel === "email") {
          await neoparentAuth.requestEmailCode(this.email);
          this.statusMessage = this.$t("auth.login.emailCodeSent");
        } else if (channel === "whatsapp") {
          await neoparentAuth.requestWhatsappCode(this.email);
          this.statusMessage = this.$t("auth.login.whatsappCodeSent");
        }else {
          await neoparentAuth.requestSmsCode(this.email);
          this.statusMessage = this.$t("auth.login.smsCodeSent");
        }
        this.canVerifyCode = true;
        this.startCooldown();
      } catch (e) {
        if (e.response?.status === 400) {
          this.error = this.$t("auth.login.smsUnavailable");
        } else {
          this.error = this.$t("auth.login.sendCodeError");
        }
      } finally {
        this.isSendingEmail = false;
        this.isSendingSms = false;
        this.isSendingWhatsapp = false;
      }
    },

    async verifyCode() {
      if (!this.email) {
        this.error = this.$t("auth.login.emailRequired");
        return;
      }

      if (!this.canVerifyCode) {
        this.error = this.$t("auth.login.requestCodeFirst");
        return;
      }

      if (!this.code) {
        this.error = this.$t("auth.login.codeRequired");
        return;
      }

      this.isVerifying = true;
      this.error = "";

      try {
        const response = await neoparentAuth.verifyCode(this.email, this.code);
        window.location.href = response.data.redirectUrl || "/";
      } catch (e) {
        if (e.response?.status === 401) {
          this.error = this.$t("auth.login.invalidCode");
        } else {
          this.error = this.$t("auth.login.verifyCodeError");
        }
      } finally {
        this.isVerifying = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.neoparent-login {
  padding-top: 40px;
}

.login-form {
  padding: 20px;
}

.language-switcher__label {
  color: #6b6b6b;
  font-size: 0.95rem;
}

.status-message {
  color: #6b6b6b;
}

.error {
  color: #c62828;
}
</style>
